import React from "react";
import { Star, BadgeCheck, Inbox } from "lucide-react";
import { initials } from "@/lib/constants";

export function UserAvatar({ src, name = "", size = 40, className = "", testId }) {
  const s = { width: size, height: size };
  return (
    <span
      data-testid={testId}
      style={s}
      className={`shrink-0 grid place-items-center rounded-full overflow-hidden bg-primary/10 text-primary font-semibold ${className}`}
    >
      {src ? (
        <img src={src} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span style={{ fontSize: size * 0.4 }}>{initials(name) || "?"}</span>
      )}
    </span>
  );
}

export function Stars({ value = 0, count, size = 14, className = "" }) {
  const rounded = Math.round(value * 2) / 2;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span className="inline-flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={size}
            strokeWidth={1.5}
            className={i <= rounded ? "fill-warning text-warning" : "text-border fill-transparent"}
          />
        ))}
      </span>
      {value > 0 && <span className="text-sm font-medium text-foreground tabular">{value.toFixed(1)}</span>}
      {count != null && <span className="text-xs text-muted-foreground">({count})</span>}
    </span>
  );
}

export function VerifiedBadge({ label = "Verified", className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 text-primary ${className}`} title="Verified expert">
      <BadgeCheck size={16} strokeWidth={2} className="fill-primary/10" />
      <span className="text-xs font-medium">{label}</span>
    </span>
  );
}

const STATUS_STYLES = {
  pending: "bg-warning/10 text-warning",
  confirmed: "bg-primary/10 text-primary",
  approved: "bg-success/10 text-success",
  completed: "bg-success/10 text-success",
  rejected: "bg-destructive/10 text-destructive",
  declined: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
  active: "bg-success/10 text-success",
};

export function StatusBadge({ status, className = "" }) {
  const cls = STATUS_STYLES[status] || "bg-muted text-muted-foreground";
  return (
    <span
      data-testid={`status-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${cls} ${className}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status}
    </span>
  );
}

export function Pill({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full bg-secondary text-secondary-foreground px-2.5 py-0.5 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, action, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center text-center rounded-[0.7rem] border border-border/60 bg-gradient-to-br from-secondary/50 to-transparent px-6 py-16"
    >
      <span className="grid place-items-center w-16 h-16 rounded-full bg-primary/[0.04]">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-primary/[0.09] text-primary">
          <Icon size={22} strokeWidth={1.5} />
        </span>
      </span>
      <h3 className="text-lg font-medium text-foreground mt-6">{title}</h3>
      {description && <p className="text-sm text-muted-foreground mt-2 max-w-sm">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, sub, accent = false, className = "" }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-[0.7rem] border p-6 shadow-sm transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-soft ${accent ? "border-primary/25 bg-primary/[0.025]" : "border-border bg-card"} ${className}`}
    >
      {accent && <span className="absolute left-0 top-0 h-full w-1 bg-primary" />}
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        {Icon && (
          <span className={`grid place-items-center w-9 h-9 rounded-lg shrink-0 transition-colors duration-200 ${accent ? "bg-primary/10 text-primary" : "bg-secondary/80 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"}`}>
            <Icon size={17} strokeWidth={1.75} />
          </span>
        )}
      </div>
      <div className="mt-3 text-[2rem] leading-none font-semibold tracking-tight tabular text-foreground">{value}</div>
      {sub && <p className="text-xs text-muted-foreground mt-3.5">{sub}</p>}
    </div>
  );
}

export function PageHeader({ eyebrow, title, description, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary mb-2">{eyebrow}</p>}
        <h1 className="text-[1.65rem] leading-tight font-semibold tracking-tight text-foreground">{title}</h1>
        {description && <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
    </div>
  );
}

export function SectionCard({ icon: Icon, title, description, action, count, children, className = "", contentClassName = "" }) {
  return (
    <section className={`card-milled rounded-[0.8rem] p-6 sm:p-7 ${className}`}>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          {Icon && (
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary/80 text-primary shrink-0">
              <Icon size={17} strokeWidth={1.75} />
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground truncate">{title}</h3>
              {count != null && count > 0 && (
                <span className="min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold tabular shrink-0">{count}</span>
              )}
            </div>
            {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className={contentClassName}>{children}</div>
    </section>
  );
}

export function Spinner({ className = "" }) {
  return <span className={`inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin ${className}`} />;
}
